import {
  LessonComponentBase,
  LessonDisplayMode
} from '../../models/LessonComponent';
import { LinearProgress, Skeleton } from '@mui/material';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import Slide, { SlideProps } from '@mui/material/Slide';
import { cloneDeep, isEqual, isFunction, isUndefined } from 'lodash';
import useUnansweredQuestions, {
  Question
} from '../../hooks/useUnansweredQuestions';

import AlertWarning from '../../../shared/components/AlertWarning';
import { ApiPortfolio } from '../../../dashboard/models/ApiPortfolio';
import { ApiPortfolioState } from '../../../dashboard/models/ApiPortfolioState';
import AssetService from '../../../shared/services/AssetService';
import CommentPanel from '../../../assessments/components/CommentPanel';
import CompletionMessage from '../../../shared/components/CompletionMessage';
import { CompletionModel } from '../../../shared/models/CompletionModel';
import CompletionPanel from './CompletionPanel';
import Dialog from '@mui/material/Dialog';
import FeatureAccessService from '../../../shared/services/FeatureAccessService';
import Grid from '@mui/material/Unstable_Grid2';
import { LessonCompletionType } from '../../models/LessonBehaviorSettings';
import { LessonComponentList } from '../LessonComponentList';
import LessonComponentService from '../../services/LessonComponentService';
import { LessonContextProvider } from '../../models/LessonContext';
import LessonDisplaySettings from '../../models/LessonDisplaySettings';
import { LessonMarkingAction } from '../LessonMarkingPanel';
import LessonPlayerAppBar from './LessonPlayerAppBar';
import LessonPortfolioService from '../../../assessments/services/LessonPortfolioService';
import LessonService from '../../services/LessonService';
import LessonServiceDashboard from '../../../dashboard/services/LessonService';
import { LessonSubmissionAction } from '../LessonSubmissionPanel';
import { LessonType } from '../../models/LessonVm';
import OverviewPanel from './OverviewPanel';
import { PortfolioAsset } from '../../../dashboard/models/PortfolioAsset';
import ProgressStatusCode from '../../../dashboard/models/ProgressStatusCode';
import dayjs from 'dayjs';
import styles from '../../styles/LessonPlayer.scss';
import { useLessonPlayerContext } from '../../../dashboard/models/LessonPlayerContext';
import { useLessonPlayerMarkingContext } from '../../../assessments/models/LessonPlayerMarkingContext';
import useLessonProgress from '../../hooks/useLessonProgress';

const Transition = React.forwardRef(
  (props: SlideProps, ref: React.Ref<unknown>) => (
    <Slide direction="right" ref={ref} {...props} />
  )
);

function LessonPlayerSkeleton() {
  const skeleton = [];
  for (let i = 0; i < 3; i++) {
    skeleton.push(
      <Grid key={i} style={{ width: '100%' }}>
        <Skeleton
          animation="wave"
          variant="rectangular"
          width="100%"
          height={160}
        />
      </Grid>
    );
    skeleton.push(
      <Grid key={`button-skeleton-${i}`}>
        <Skeleton
          animation="wave"
          variant="rectangular"
          width={200}
          height={50}
        />
      </Grid>
    );
  }

  return (
    <Grid
      container
      direction="column"
      alignItems="center"
      spacing={2}
      style={{ width: '100%' }}
    >
      {skeleton}
    </Grid>
  );
}

export interface LessonPlayerProps {
  loading: boolean;
  open: boolean;
  displayMode: LessonDisplayMode;
  layer?: number; // Applicable for learner's dashboard
  lessonVersion?: number; // Applicable for learner's dashboard
  lessonkey: number;
  lessonTitle: string;
  lessonDescription: string;
  lessonType: LessonType; //* for Bracken 2 only
  displaySettings: LessonDisplaySettings;
  components: LessonComponentBase[];
  state: ApiPortfolioState;
  complete?: boolean;
  status?: ProgressStatusCode;
  hasCert?: boolean;
  required?: boolean; // This property is used to decide showing `Continue to next lesson`.
  lastLesson?: boolean; // This property is used to decide showing `Continue to next lesson`.
  timeToComplete?: number; // Module completion time.
  completionType: LessonCompletionType;
  completionThreshold: number;
  inProgress?: boolean;
  /** 'only for 'learner' view */
  portfolioModified?: Date;
  portfolioAssets?: PortfolioAsset;
  portfolioLocked?: boolean; // disable old version portfolio in learner's dashboard
  /** 'assessment' type only */
  portfolio?: ApiPortfolio;
  /** 'assessment' type only */
  learner?: string;
  /** 'assessment' type only */
  marker?: string;
  onSubmitAnswer?: (
    componentkey: number,
    answer: string,
    points: number
  ) => void; //* used just in the 'learn' display mode.
  onComplete?: (completed: boolean) => void; //* used just in the 'learn' display mode
  onSubmitLesson?: (action: LessonSubmissionAction) => void; //* used just with the 'assessment' lesson type in the 'learn' display mode.
  onMarkLesson?: (action: LessonMarkingAction, completed: boolean) => void; //* used just in the 'mark' display mode.
  onUpdateMark?: (componentKey: number, mark: number) => void; //* used only in 'mark' display mode
  onClose: () => void;
  onContinueNextLesson?: (lessonCompleted: boolean) => void; // Applicable for learner mode only.
}

const LessonPlayer = (props: LessonPlayerProps) => {
  const isPreview = props.displayMode === 'preview';
  const portfolioLocked = props.portfolioLocked;
  const [components, setComponents] = useState<LessonComponentBase[]>(
    props.components
  );
  const [editComponents, setEditComponents] = useState<LessonComponentBase[]>(
    props.components
  ); //* keeping editComponents as separate so it can be used in the child components instead of maintaining
  //* the user response details in a separate state
  const [canComplete, setCanComplete] = useState(true);
  const [lessonCompleted, setLessonCompleted] = useState<boolean | null>(
    props.complete
  );

  const [showWarning, setShowWarning] = useState(false);
  const [questionsNotsaved, setQuestionsNotsaved] = useState<Question[]>([]);
  const [unansweredQuestions, setUnansweredQuestions] = useState<Question[]>(
    []
  );

  const [quizLocked, setQuizLocked] = useState(
    props.state === ApiPortfolioState.QuizLock
  );
  const [assessmentSubmitted, setAssessmentSubmitted] = useState(
    LessonPortfolioService.hasSubmitted(props.state)
  );
  const [finalized, setFinalized] = useState(false); //* Set to true when the lesson is completed/finalized.
  const [backgroundStyle, setBackgroundStyle] = useState<CSSProperties>();
  const [showCommentPanel, setShowCommentPanel] = useState(
    LessonService.showCommentMenu(props.displayMode, props.state)
  );
  const [assessmentRevised, setAssessmentRevised] = useState(false);
  const [completionMessage, setCompletionMessage] =
    useState<CompletionModel>(null);

  const { assignedToMe } = useLessonPlayerMarkingContext();
  const { lessonState } = useLessonPlayerContext();
  const scrollRef = useRef<HTMLDivElement>(null);

  useLessonProgress(
    props.displayMode,
    scrollRef,
    editComponents,
    lessonState,
    finalized,
    props.portfolioAssets,
    portfolioLocked || quizLocked || assessmentSubmitted
  );

  const { getUnanswered } = useUnansweredQuestions();

  useEffect(() => {
    //show/hide comment panel to regular markers depending on whether the assessment is assigned to them or not
    if (
      !FeatureAccessService.hasMasterAccess &&
      !FeatureAccessService.hasMasterMarkerAccess &&
      props.displayMode === 'mark'
    ) {
      setShowCommentPanel(assignedToMe);
    }
  }, [assignedToMe]);

  useEffect(() => {
    /* set quizLocked to true to lock the quiz once finalized in the preview */
    if (finalized && isPreview && props.lessonType === 'quiz') {
      setQuizLocked(true);
    }
  }, [finalized]);

  useEffect(() => {
    setQuizLocked(props.state === ApiPortfolioState.QuizLock);
    setAssessmentSubmitted(LessonPortfolioService.hasSubmitted(props.state));
  }, [props.state]);

  useEffect(() => {
    setFinalized(
      !isPreview &&
        ((props.complete && props.lessonType === 'lesson') ||
          (props.lessonType === 'quiz' &&
            props.state === ApiPortfolioState.QuizLock) ||
          (LessonPortfolioService.hasSubmitted(props.state) &&
            props.lessonType === 'assessment') ||
          props.displayMode ===
            'mark') /** set finalized to true for 'mark' mode in all states. so that it will stay in the collapsed lesson 
          when the lesson is opened or when 'Request more work' is clicked for the marker */
    );
  }, [props.complete, props.lessonType, props.state]);

  useEffect(() => {
    //* set components state whenever the db value changes
    //* set editComponents only at the initial time.
    setComponents(props.components);
    setEditComponents(props.components);
  }, [props.components]);

  useEffect(() => {
    if (props.displaySettings) {
      const displaySettings = props.displaySettings;
      if (!displaySettings) {
        return;
      }
      setBackgroundStyle({
        backgroundColor:
          displaySettings.background?.type === 'color'
            ? displaySettings.background?.value || 'ihherit'
            : 'inherit',
        backgroundImage:
          displaySettings.background?.type === 'image'
            ? `url(${AssetService.getAssetUrl(
                displaySettings.background?.value
              )})` || 'ihherit'
            : 'inherit',
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      });
    }
  }, [props.displaySettings]);

  useEffect(() => {
    if (props.portfolio) {
      setLessonCompleted(!!props.portfolio.completed);
    }
  }, [props.portfolio]);

  function getLessonTotalPoints() {
    return LessonComponentService.getLessonTotalPoints(
      props.lessonType,
      components
    );
  }

  function getUserTotalPoints() {
    return LessonComponentService.getUserTotalPoints(components);
  }

  function handleLockChange(anyLocked: boolean) {
    setCanComplete(!anyLocked);
  }

  function handleChangeAnswer(componentkey: number, answer: string) {
    setEditComponents((value) => {
      const copy = cloneDeep(value);
      const component = copy.find((c) => c.componentkey === componentkey);
      if (isUndefined(component)) {
        throw new Error('Missing component. Something went wrong!');
      }
      component.answer = answer;
      return copy;
    });
  }

  function handleResponse(
    componentkey: number,
    answer: string,
    points: number
  ) {
    setComponents((value) => {
      const copy = cloneDeep(value);
      const component = copy.find((c) => c.componentkey === componentkey);
      if (isUndefined(component)) {
        throw new Error('Missing component. Something went wrong!');
      }
      component.answer = answer;
      component.mark = points;
      return copy;
    });

    if (!isPreview && isFunction(props.onSubmitAnswer)) {
      props.onSubmitAnswer(componentkey, answer, points);
    }
  }

  function handleReturn() {
    setFinalized(false);
    setEditComponents(components);
  }

  const handleComplete = () => {
    //Get any unanswered or unsaved questions from useUnansweredQuestions
    const { unanswered, unsaved } = getUnanswered(components, editComponents);

    if (unanswered.length > 0 || !isEqual(unanswered, unansweredQuestions)) {
      setUnansweredQuestions(unanswered);
    }
    if (unsaved.length > 0 || !isEqual(unsaved, questionsNotsaved)) {
      setQuestionsNotsaved(unsaved);
    }
    if (props.displayMode !== 'mark' && (unanswered.length || unsaved.length)) {
      setShowWarning(true);
    } else {
      handleCompleteOverview();
    }
  };

  function handleCompleteOverview() {
    //* The user has reached the end of a lesson.
    //* If completion condition is 'complete at end', call the complete endpoint.
    setFinalized(true);
    setShowWarning(false);
    /* set assessmentRevised in the 'work requested' state to show the total points in the collaped lesson page only 
    if none of the components is re-answered */
    if (
      props.portfolioModified &&
      props.state === ApiPortfolioState.AssessmentRetry
    ) {
      setAssessmentRevised(
        components?.some(
          (c) => dayjs(c.answerModified).toDate() > props.portfolioModified
        )
      );
    }

    const completed = props.portfolio
      ? !!props.portfolio.completed
      : LessonServiceDashboard.hasLessonCompleted(
          props.completionType,
          props.completionThreshold,
          getUserTotalPoints(),
          getLessonTotalPoints()
        );
    setLessonCompleted(completed);

    //Get lesson completion display message for basic lesson and quiz lesson type
    const completionInfo: CompletionModel =
      LessonServiceDashboard.getCompletionMessage(props.lessonType, completed);
    setCompletionMessage(completionInfo);
    if (
      !isPreview &&
      ((!props.complete && props.lessonType === 'lesson' && completed) || //For basic lesson type, onComplete needs to be called only when the threshold is met
        (!quizLocked && props.lessonType === 'quiz')) && //For quiz type, onComplete doesn't need to be called once it is locked
      isFunction(props.onComplete)
    ) {
      //* only in the 'learn' display mode.
      //* The 'assessment' lesson type doesn't complete a lesson in the 'learn' display mode.
      props.onComplete(completed);
    }
  }

  function handleSubmitLesson(action: LessonSubmissionAction) {
    if (isPreview) {
      setFinalized(true);
      setAssessmentSubmitted(true);
    }
    if (isFunction(props.onSubmitLesson)) {
      props.onSubmitLesson(action);
    }
  }

  function handleMarkLesson(action: LessonMarkingAction) {
    const completed = LessonServiceDashboard.hasLessonCompleted(
      props.completionType,
      props.completionThreshold,
      getUserTotalPoints(),
      getLessonTotalPoints()
    );
    props.onMarkLesson(action, completed);
  }

  const handleContinue = () => {
    props.onContinueNextLesson(lessonCompleted);
    setCompletionMessage(null);
  };

  const handleClose = () => {
    props.onClose();
    setFinalized(false);
    if (isPreview) {
      //setting the components state to initial state
      setComponents(props.components);
      setEditComponents(props.components);
    }
  };

  const goToQuestion = async (id: number) => {
    const element = document.getElementById(`${id}`);
    if (element) {
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth' });
      }, 50);
    }
  };

  const showContinue =
    isFunction(props.onContinueNextLesson) &&
    !props.lastLesson &&
    ((props.required && props.complete) || !props.required);

  const contextValues = {
    mode: props.displayMode,
    lessonType: props.lessonType,
    finalized: finalized,
    quizLocked: quizLocked,
    assessmentSubmitted: assessmentSubmitted,
    assessmentRevised,
    portfolioLocked
  };

  return (
    <Dialog
      fullScreen
      open={props.open}
      TransitionComponent={Transition}
      PaperProps={{ sx: { overflow: 'unset' } }}
    >
      <LessonPlayerAppBar
        {...props}
        onClose={handleClose}
        setShowCommentPanel={setShowCommentPanel}
      />

      {props.inProgress && <LinearProgress />}
      <LessonContextProvider value={contextValues}>
        <div
          className={styles.componentListContainer}
          style={backgroundStyle}
          ref={scrollRef}
        >
          {showCommentPanel && <CommentPanel marker={props.marker} />}
          <div
            className={`${styles.componentList} ${
              (LessonComponentService.hasFullwidthComponent(components) ||
                LessonComponentService.hasStretchBackground(components)) &&
              !finalized
                ? ''
                : styles.componentListWithMaxWidth
            } ${
              finalized && completionMessage ? styles.showCompletionMessage : ''
            }`}
            id="quill_toolbar_player"
            style={{
              backgroundColor: props.displaySettings?.background?.containerColor
            }}
          >
            {props.loading && <LessonPlayerSkeleton />}
            <LessonComponentList
              components={components}
              editComponents={editComponents}
              lessonTitle={props.lessonTitle}
              goToQuestion={goToQuestion}
              onLockChange={handleLockChange}
              onResponse={handleResponse}
              onChangeAnswer={handleChangeAnswer}
              onUpdateMark={props.onUpdateMark}
            />
            {!props.loading && !finalized && canComplete && (
              <CompletionPanel
                lessonType={props.lessonType}
                preview={isPreview}
                complete={props.complete}
                quizLocked={quizLocked}
                lessonCompleted={lessonCompleted}
                portfolioLocked={portfolioLocked}
                onComplete={handleComplete}
              />
            )}
            {finalized && (
              <OverviewPanel
                lessonType={props.lessonType}
                displayMode={props.displayMode}
                inProgress={props.inProgress}
                points={getUserTotalPoints()}
                totalPoints={getLessonTotalPoints()}
                completed={lessonCompleted}
                showContinue={showContinue}
                state={props.state}
                onClose={handleClose}
                onReturn={handleReturn}
                onSubmit={handleSubmitLesson}
                onMark={handleMarkLesson}
                onContinue={handleContinue}
              />
            )}
            {completionMessage && !portfolioLocked && (
              <CompletionMessage
                completionStatus={completionMessage}
                isCompleted={
                  (props.lessonType === 'lesson' && lessonCompleted) ||
                  (props.lessonType === 'quiz' && quizLocked)
                }
                hasCert={props.hasCert}
                status={props.status}
                showContinue={showContinue}
                userPoints={getUserTotalPoints()}
                assignedPoints={getLessonTotalPoints()}
                handleReturnToLesson={() => setCompletionMessage(null)}
                handleReturnToCourse={handleClose}
                handleContinue={handleContinue}
              />
            )}
            {showWarning && (
              <AlertWarning
                showOpen={showWarning}
                questionsNotsaved={questionsNotsaved}
                unansweredQuestions={unansweredQuestions}
                goToQuestion={async (id: number) => {
                  await goToQuestion(id);
                  setShowWarning(false);
                }}
                handleClose={() => setShowWarning(false)}
                handleContinue={handleCompleteOverview}
              />
            )}
          </div>
        </div>
      </LessonContextProvider>
    </Dialog>
  );
};

export default LessonPlayer;
